import { LRTLogo } from '@/components/LRTLogo';
import { AppStoreBadges } from '@/components/brand/AppStoreBadges';
import { PhoneInput } from '@/components/ui/phone-input';
import { Slider } from '@/components/ui/slider';
import { trackFormSubmitted } from '@/lib/analytics-tracker';
import { ApiFetchError, apiFetch } from '@/lib/api-fetch';
import { ArrowLeft, Camera, MessageSquare, Phone } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { InquiryContext } from './InquiryModal';

interface DecorationImage {
    image_url: string | null;
    credit?: string | null;
    credit_url?: string | null;
    caption?: string | null;
}

// ── Validators ───────────────────────────────────────────────────

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validateField(field: 'first_name' | 'last_name' | 'email' | 'phone', value: string): string | null {
    const trimmed = value.trim();
    switch (field) {
        case 'first_name':
            if (trimmed.length < 1) return 'Please enter your first name.';
            return null;
        case 'last_name':
            if (trimmed.length < 1) return 'Please enter your last name.';
            return null;
        case 'email':
            if (trimmed.length < 1) return 'Please enter your email.';
            if (!EMAIL_RE.test(trimmed)) return 'Please enter a valid email address.';
            return null;
        case 'phone': {
            const digits = trimmed.replace(/\D/g, '');
            if (digits.length < 7) return 'Please enter a valid phone number.';
            if (digits.length > 15) return 'Please enter a valid phone number.';
            return null;
        }
    }
}

// ── Budget tiers ─────────────────────────────────────────────────

const BUDGET_TIERS = [
    'Under $5,000',
    '$5,000 – $10,000',
    '$10,000 – $25,000',
    '$25,000 – $50,000',
    '$50,000 – $100,000',
    '$100,000 – $250,000',
    '$250,000+',
];

// ── Style tokens ─────────────────────────────────────────────────

const INPUT_STYLE: React.CSSProperties = {
    border: '0.5px solid rgba(245, 245, 245, 0.30)',
    borderRadius: '6px',
    padding: '8px 14px',
    height: '36px',
    fontSize: '14px',
    fontFamily: "'Inter', sans-serif",
    color: '#F5F5F5',
    outline: 'none',
    width: '100%',
    backgroundColor: 'transparent',
    transition: 'border-color 150ms ease',
};

const TEXTAREA_STYLE: React.CSSProperties = {
    ...INPUT_STYLE,
    height: 'auto',
    minHeight: '72px',
    maxHeight: '300px',
    resize: 'none',
    overflowY: 'auto',
};

const LABEL_CLS = 'mb-1.5 block text-[11px] tracking-[0.15em] uppercase';
const LABEL_STYLE: React.CSSProperties = { color: 'rgba(245, 245, 245, 0.7)' };

// ── Props ────────────────────────────────────────────────────────

interface InquiryFormProps {
    context: InquiryContext;
    showLogo?: boolean;
    title?: string;
    hideTrustBadges?: boolean;
    hideHeader?: boolean;
    /**
     * Render the "Or register in the app" divider + store badges between the
     * submit button and the trust logos. Only the /clients page and the modal
     * opened from the homepage strip turn this on — journal and agent openings
     * stay app-free until the CRM's tokenised agent links exist (22 Sep 2026).
     */
    showAppDownload?: boolean;
}

// ── Component ────────────────────────────────────────────────────

export function InquiryForm({
    context,
    showLogo = false,
    title,
    hideTrustBadges = false,
    hideHeader = false,
    showAppDownload = false,
}: InquiryFormProps) {
    const [form, setForm] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        budget: BUDGET_TIERS[3],
        additional_info: '',
        newsletter_opt_in: false,
        _hp_field: '',
    });
    const [budgetIndex, setBudgetIndex] = useState(3);
    const [mode, setMode] = useState<'form' | 'choice'>('form');
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [fieldErrors, setFieldErrors] = useState<Partial<Record<'first_name' | 'last_name' | 'email' | 'phone', string>>>({});
    const [touched, setTouched] = useState<Partial<Record<'first_name' | 'last_name' | 'email' | 'phone', boolean>>>({});
    const [decoration, setDecoration] = useState<DecorationImage | null>(null);

    // Preload a curated hero image so the success state has editorial weight
    useEffect(() => {
        let cancelled = false;
        fetch('/api/inquiry-decoration-image', { credentials: 'include' })
            .then((r) => (r.ok ? r.json() : null))
            .then((data) => {
                if (!cancelled && data?.image_url) setDecoration(data);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, []);

    // No auto-close on success — the modal stays open until the user
    // dismisses it via the X button. Reserves user agency over the
    // dismiss moment so the success state can be read at their pace.

    const updateField = (field: string, value: string) => {
        setForm((prev) => ({ ...prev, [field]: value }));
        setError(null);
        if (field === 'first_name' || field === 'last_name' || field === 'email' || field === 'phone') {
            // Phone input is a compound widget (country picker + text); treat any
            // meaningful change as touched so errors surface inline.
            const shouldMarkTouched = field === 'phone' && value.replace(/\D/g, '').length >= 3;
            const isTouched = touched[field] || shouldMarkTouched;
            if (shouldMarkTouched && !touched[field]) {
                setTouched((prev) => ({ ...prev, [field]: true }));
            }
            if (isTouched) {
                setFieldErrors((prev) => ({ ...prev, [field]: validateField(field, value) ?? undefined }));
            }
        }
    };

    const handleBlur = (field: 'first_name' | 'last_name' | 'email' | 'phone') => {
        setTouched((prev) => ({ ...prev, [field]: true }));
        setFieldErrors((prev) => ({ ...prev, [field]: validateField(field, form[field]) ?? undefined }));
    };

    const isFormValid = (() => {
        const fields: Array<'first_name' | 'last_name' | 'email' | 'phone'> = ['first_name', 'last_name', 'email', 'phone'];
        return fields.every((f) => validateField(f, form[f]) === null);
    })();

    const handleFormSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        // Validate everything before advancing
        const fields: Array<'first_name' | 'last_name' | 'email' | 'phone'> = ['first_name', 'last_name', 'email', 'phone'];
        const nextErrors: typeof fieldErrors = {};
        let hasError = false;
        fields.forEach((f) => {
            const err = validateField(f, form[f]);
            if (err) {
                nextErrors[f] = err;
                hasError = true;
            }
        });
        if (hasError) {
            setFieldErrors(nextErrors);
            setTouched({ first_name: true, last_name: true, email: true, phone: true });
            return;
        }
        setMode('choice');
    };

    const submitInquiry = async (contactPreference: 'text' | 'call') => {
        setSubmitting(true);
        setError(null);

        try {
            await apiFetch('/api/inquiries', {
                method: 'POST',
                body: JSON.stringify({
                    ...form,
                    contact_preference: contactPreference,
                    newsletter_opt_in: form.newsletter_opt_in,
                    context_source: context.source,
                    context_agent_id: context.agent_id || null,
                    context_article_id: context.article_id || null,
                    context_influencer_id: context.influencer_id || null,
                    context_influencer_name: context.influencer_name || null,
                    context_page_url: context.page_url,
                }),
                silent: true,
            });

            setSuccess(true);
            // Single unified form-event push: trackFormSubmitted handles both
            // the GTM dataLayer push (event: 'form_submitted') AND the internal
            // analytics_events row. All inquiry-specific context flows through
            // eventData so a single GA4 tag listening on form_submitted gets
            // the full picture (source, agent/article context, budget, etc.).
            trackFormSubmitted('inquiry', {
                form_label: 'Speak with an Advisor',
                inquiry_source: context.source ?? null,
                inquiry_agent_id: context.agent_id ?? null,
                inquiry_article_id: context.article_id ?? null,
                inquiry_influencer_id: context.influencer_id ?? null,
                inquiry_budget: form.budget,
                inquiry_contact_preference: contactPreference,
            });
        } catch (err) {
            // apiFetch throws on every non-2xx — distinguish by status so the
            // user sees something accurate instead of a misleading "connection"
            // message for what's actually a server-side response.
            if (err instanceof ApiFetchError) {
                const body = err.body as { message?: string } | undefined;
                if (err.status === 429) {
                    setError('Too many submissions — please wait a moment and try again.');
                } else if (err.status === 422) {
                    setError(body?.message || 'Please review your information and try again.');
                } else if (err.status > 0) {
                    setError(body?.message || 'Something went wrong. Please try again.');
                } else {
                    setError('Unable to submit. Please check your connection and try again.');
                }
            } else {
                setError('Unable to submit. Please check your connection and try again.');
            }
            setMode('form');
        } finally {
            setSubmitting(false);
        }
    };

    const hasAgent = !!(context.agent_id && context.agent_name);
    const reachOutCopy = hasAgent ? `${context.agent_name} will reach out shortly.` : 'Your advisor will reach out shortly.';

    if (success) {
        return (
            <div className="text-center">
                <p className="text-3xl font-normal" style={{ fontFamily: "'Didot', 'Playfair Display', Georgia, serif", color: '#F5F5F5' }}>
                    Thank you, {form.first_name}.
                </p>
                <p className="mt-2 text-sm" style={{ color: 'rgba(245, 245, 245, 0.55)' }}>
                    {reachOutCopy}
                </p>

                {decoration?.image_url && (
                    <div className="mt-10 text-left">
                        <img src={decoration.image_url} alt="" className="w-full rounded-md object-cover" style={{ aspectRatio: '3 / 2' }} />
                        {(decoration.credit || decoration.caption) && (
                            <div
                                className="mt-2 font-sans text-[11px]"
                                style={{ fontFamily: "'Inter', sans-serif", color: 'rgba(245, 245, 245, 0.55)' }}
                            >
                                {decoration.credit && (
                                    <span className="inline-flex items-center gap-1">
                                        <Camera size={12} strokeWidth={1.5} style={{ stroke: 'rgba(245, 245, 245, 0.55)' }} />
                                        {decoration.credit_url ? (
                                            <a
                                                href={decoration.credit_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="transition-opacity hover:opacity-80"
                                                style={{ color: 'rgba(245, 245, 245, 0.55)' }}
                                            >
                                                {decoration.credit}
                                            </a>
                                        ) : (
                                            <span>{decoration.credit}</span>
                                        )}
                                    </span>
                                )}
                                {decoration.caption && (
                                    <span className="mt-1 block text-[11px] tracking-[0.2em] uppercase" style={{ color: 'rgba(245, 245, 245, 0.4)' }}>
                                        {decoration.caption}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }

    return (
        <>
            {/* Header */}
            {!hideHeader && (
                <div className="mb-6 text-center">
                    {showLogo && <LRTLogo className="mx-auto mb-5 h-6 w-auto" />}
                    <h2
                        className="text-2xl font-normal md:text-[28px]"
                        style={{ fontFamily: "'Didot', 'Playfair Display', Georgia, serif", color: '#F5F5F5' }}
                    >
                        {title ?? (hasAgent ? `Book with ${context.agent_name}` : 'Speak with an Advisor')}
                    </h2>
                    <p
                        className="mx-auto mt-3 text-[13px] leading-relaxed"
                        style={{ fontFamily: "'Inter', sans-serif", color: 'rgba(245, 245, 245, 0.65)' }}
                    >
                        Trusted by family offices, executives, and industry leaders. Your information is kept strictly confidential. No fees. No
                        membership required.
                    </p>
                </div>
            )}

            <form onSubmit={handleFormSubmit} noValidate className="@container/inquiry">
                {/* Honeypot */}
                <input
                    type="text"
                    name="_hp_field"
                    value={form._hp_field}
                    onChange={(e) => updateField('_hp_field', e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                    style={{ position: 'absolute', left: '-9999px', opacity: 0 }}
                />

                {mode === 'form' ? (
                    <>
                        <div className="mb-4 grid grid-cols-1 gap-4 @[340px]/inquiry:grid-cols-2">
                            <div>
                                <label className={LABEL_CLS} style={LABEL_STYLE}>
                                    First Name
                                </label>
                                <input
                                    type="text"
                                    value={form.first_name}
                                    onChange={(e) => updateField('first_name', e.target.value)}
                                    onBlur={() => handleBlur('first_name')}
                                    required
                                    aria-invalid={!!fieldErrors.first_name}
                                    className="inquiry-field"
                                    style={INPUT_STYLE}
                                />
                                {fieldErrors.first_name && (
                                    <p className="mt-1.5 text-[11px]" style={{ color: '#E87C7C' }}>
                                        {fieldErrors.first_name}
                                    </p>
                                )}
                            </div>
                            <div>
                                <label className={LABEL_CLS} style={LABEL_STYLE}>
                                    Last Name
                                </label>
                                <input
                                    type="text"
                                    value={form.last_name}
                                    onChange={(e) => updateField('last_name', e.target.value)}
                                    onBlur={() => handleBlur('last_name')}
                                    required
                                    aria-invalid={!!fieldErrors.last_name}
                                    className="inquiry-field"
                                    style={INPUT_STYLE}
                                />
                                {fieldErrors.last_name && (
                                    <p className="mt-1.5 text-[11px]" style={{ color: '#E87C7C' }}>
                                        {fieldErrors.last_name}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="mb-4 grid grid-cols-1 gap-4 @[420px]/inquiry:grid-cols-2">
                            <div>
                                <label className={LABEL_CLS} style={LABEL_STYLE}>
                                    Email
                                </label>
                                <input
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => updateField('email', e.target.value)}
                                    onBlur={() => handleBlur('email')}
                                    required
                                    aria-invalid={!!fieldErrors.email}
                                    className="inquiry-field"
                                    style={INPUT_STYLE}
                                />
                                {fieldErrors.email && (
                                    <p className="mt-1.5 text-[11px]" style={{ color: '#E87C7C' }}>
                                        {fieldErrors.email}
                                    </p>
                                )}
                            </div>
                            <div>
                                <label className={LABEL_CLS} style={LABEL_STYLE}>
                                    Phone
                                </label>
                                <PhoneInput
                                    value={form.phone}
                                    onChange={(val) => updateField('phone', val)}
                                    placeholder="(555) 123-4567"
                                    required
                                    aria-invalid={!!fieldErrors.phone}
                                    buttonClassName="h-[36px] rounded-[6px] border-[0.5px] border-[rgba(245,245,245,0.30)] bg-transparent text-[#F5F5F5] hover:bg-[rgba(245,245,245,0.08)]"
                                    buttonStyle={{ color: '#F5F5F5' }}
                                    inputClassName="h-[36px] rounded-[6px] border-[0.5px] border-[rgba(245,245,245,0.30)] bg-transparent text-[#F5F5F5] placeholder:text-[rgba(245,245,245,0.55)] focus-visible:border-[rgba(245,245,245,0.55)] focus-visible:ring-0 transition-[border-color] duration-150"
                                    inputStyle={{ fontFamily: "'Inter', sans-serif", fontSize: '14px' }}
                                />
                                {fieldErrors.phone && (
                                    <p className="mt-1.5 text-[11px]" style={{ color: '#E87C7C' }}>
                                        {fieldErrors.phone}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="mb-5">
                            <div className="mb-2.5 flex items-baseline justify-between">
                                <label className="text-[11px] tracking-[0.15em] uppercase" style={LABEL_STYLE}>
                                    Trip Budget
                                </label>
                                <span className="text-sm" style={{ color: 'rgba(245, 245, 245, 0.75)' }}>
                                    {form.budget}
                                </span>
                            </div>
                            <Slider
                                min={0}
                                max={6}
                                step={1}
                                value={[budgetIndex]}
                                onValueChange={([idx]) => {
                                    setBudgetIndex(idx);
                                    updateField('budget', BUDGET_TIERS[idx]);
                                }}
                                aria-label="Trip budget"
                                className="[&_[data-slot=slider-track]]:!h-1 [&_[data-slot=slider-track]]:bg-[#F5F5F5]/12 [&_[data-slot=slider-range]]:bg-[#F5F5F5]/40 [&_[data-slot=slider-thumb]]:border-[#F5F5F5]/40 [&_[data-slot=slider-thumb]]:bg-obsidian [&_[data-slot=slider-thumb]]:shadow-[0_2px_4px_rgba(0,0,0,0.5)]"
                            />
                        </div>

                        <div className="mb-6">
                            <label className={LABEL_CLS} style={LABEL_STYLE}>
                                Tell Us About Your Next Trip
                            </label>
                            <textarea
                                value={form.additional_info}
                                onChange={(e) => {
                                    updateField('additional_info', e.target.value);
                                    // Auto-grow: reset to auto so scrollHeight reflects real content,
                                    // then snap to scrollHeight. Lets the field accommodate however
                                    // much the client wants to share without a drag handle.
                                    e.target.style.height = 'auto';
                                    e.target.style.height = `${e.target.scrollHeight}px`;
                                }}
                                rows={3}
                                maxLength={2000}
                                placeholder="Your dates, who's joining, destinations on your mind, favorite hotels or preferences..."
                                className="inquiry-field"
                                style={TEXTAREA_STYLE}
                            />
                        </div>

                        {/* Editorial dispatch opt-in — temporarily hidden, restore when newsletter program launches */}
                        {/* <label className="mb-6 flex cursor-pointer items-start gap-2.5 text-[12px] leading-snug">
                            <input
                                type="checkbox"
                                checked={form.newsletter_opt_in}
                                onChange={(e) => setForm((prev) => ({ ...prev, newsletter_opt_in: e.target.checked }))}
                                className="mt-[3px] h-[14px] w-[14px] cursor-pointer accent-[#F5F5F5]"
                            />
                            <span style={{ color: 'rgba(245, 245, 245, 0.7)', fontFamily: "'Inter', sans-serif" }}>
                                Send me LuxRally's editorial dispatch — destination ideas, advisor picks, and partner offers.
                            </span>
                        </label> */}

                        {error && (
                            <p className="mb-5 text-center text-sm" style={{ color: '#E87C7C' }}>
                                {error}
                            </p>
                        )}

                        <div className="mb-10 flex justify-center">
                            <button
                                type="submit"
                                aria-disabled={!isFormValid}
                                className="lux-caps lux-caps-sm h-auto w-full cursor-pointer rounded-sm border border-[#F5F5F5]/30 bg-[#081c1c] px-4 py-2.5 text-[#F5F5F5] transition-colors duration-300 hover:border-[#F5F5F5]/50 hover:bg-[#0a2020] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F5F5F5]/60 sm:w-auto sm:py-1"
                            >
                                {hasAgent ? 'Connect Now' : 'Meet Your Advisor'}
                            </button>
                        </div>

                        {showAppDownload && (
                            <div className="mb-12" data-testid="inquiry-app-download">
                                {/* Hairline divider with the caps label inset, then the badge pair —
                                    mirrors client-page-app-option.png. */}
                                <div className="flex items-center gap-4">
                                    <span aria-hidden className="h-px flex-1 bg-[#F5F5F5]/20" />
                                    <span className="lux-caps whitespace-nowrap text-[10px] text-[#F5F5F5]/50" style={{ letterSpacing: '0.22em' }}>
                                        Or register in the app
                                    </span>
                                    <span aria-hidden className="h-px flex-1 bg-[#F5F5F5]/20" />
                                </div>
                                <AppStoreBadges height={40} className="mt-8 justify-center gap-3.5" />
                            </div>
                        )}

                        {!hideTrustBadges && (
                            <div className="flex items-center justify-center gap-6">
                                <img
                                    src="/EndorsedByLogo-FTG-REV.png"
                                    alt="Forbes Travel Guide Endorsed"
                                    className="h-9 w-auto object-contain opacity-50"
                                />
                                <img src="/iata.svg" alt="IATA" className="h-6 w-auto object-contain opacity-50 brightness-0 invert" />
                                <img
                                    src="/TLN-logo.png"
                                    alt="Travel Leaders Network"
                                    className="h-6 w-auto object-contain opacity-50 brightness-0 invert"
                                />
                                <img src="/arc.png" alt="ARC" className="h-6 w-auto object-contain opacity-50 brightness-0 invert" />
                            </div>
                        )}
                    </>
                ) : (
                    <div className="py-4">
                        <p
                            className="mb-6 text-center text-[13px] leading-relaxed"
                            style={{ fontFamily: "'Inter', sans-serif", color: 'rgba(245, 245, 245, 0.7)' }}
                        >
                            How would you like to hear from {hasAgent ? context.agent_name?.split(' ')[0] : 'your advisor'}?
                        </p>

                        <div className="mx-auto grid max-w-xs grid-cols-2 gap-3">
                            <button
                                type="button"
                                onClick={() => submitInquiry('text')}
                                disabled={submitting}
                                className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-sm px-4 py-5 transition-colors disabled:opacity-50"
                                style={{
                                    backgroundColor: 'rgba(245, 245, 245, 0.1)',
                                    color: '#F5F5F5',
                                    border: '1px solid rgba(245, 245, 245, 0.30)',
                                }}
                            >
                                <MessageSquare size={22} strokeWidth={1.5} />
                                <span className="text-[11px] font-medium tracking-[0.25em] uppercase">Text Me</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => submitInquiry('call')}
                                disabled={submitting}
                                className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-sm px-4 py-5 transition-colors disabled:opacity-50"
                                style={{
                                    backgroundColor: 'rgba(245, 245, 245, 0.1)',
                                    color: '#F5F5F5',
                                    border: '1px solid rgba(245, 245, 245, 0.30)',
                                }}
                            >
                                <Phone size={22} strokeWidth={1.5} />
                                <span className="text-[11px] font-medium tracking-[0.25em] uppercase">Call Me</span>
                            </button>
                        </div>

                        {error && (
                            <p className="mt-4 text-center text-sm" style={{ color: '#E87C7C' }}>
                                {error}
                            </p>
                        )}

                        <button
                            type="button"
                            onClick={() => setMode('form')}
                            disabled={submitting}
                            className="mx-auto mt-5 flex cursor-pointer items-center gap-1.5 text-[11px] tracking-[0.2em] uppercase transition-colors disabled:opacity-50"
                            style={{ color: 'rgba(245, 245, 245, 0.45)' }}
                        >
                            <ArrowLeft size={12} />
                            Back
                        </button>
                    </div>
                )}
            </form>
        </>
    );
}
